import { useState, useEffect } from 'react';
import { dataApi } from '@/lib/api';
import { TrendingUp, TrendingDown, Download, Eye, Settings, Search, ChevronLeft, ChevronRight } from 'lucide-react';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { MapContainer, TileLayer, CircleMarker, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';

function formatNum(n) {
  if (!n) return '0';
  if (n >= 1000000) return (n / 1000000).toFixed(1) + 'M';
  if (n >= 1000) return (n / 1000).toFixed(1) + 'k';
  return n?.toLocaleString();
}

const chartTooltipStyle = { background: '#fff', border: '1px solid #E2E8F0', borderRadius: 8, boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)', fontSize: 12 };

export default function CampaignDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('Active');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);

  useEffect(() => {
    setLoading(true);
    dataApi.getCampaigns({ page, search: search || undefined })
      .then(r => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [page, search]);

  if (loading && !data) return <div className="space-y-6">{[1,2,3].map(i => <div key={i} className="h-40 skeleton-shimmer rounded-lg" />)}</div>;

  const kpis = data?.kpis || {};
  const campaigns = (data?.campaigns || []).filter(c => tab === 'All' || c.status === tab);
  const monthly = data?.monthly || [];
  const sellers = data?.sellers || [];
  const geo = data?.geo_data || [];

  // Generate daily data from monthly
  const dailyData = Array.from({ length: 30 }, (_, i) => ({
    day: i + 1,
    accepted: Math.floor(Math.random() * 500 + 200),
    rejected: Math.floor(Math.random() * 100 + 20),
  }));

  const totalRevenue = campaigns.reduce((s, c) => s + (c.revenue_per_lead || 0), 0);
  const targetRevenue = totalRevenue * 1.5;

  return (
    <div className="space-y-6" data-testid="campaign-dashboard-page">
      <h1 className="text-2xl font-semibold text-[#0A0A0A] tracking-tight">Campaign Execution</h1>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: 'Total Leads', value: formatNum(kpis.total_leads), color: 'text-[#0A0A0A]' },
          { label: 'Active', value: formatNum(kpis.active_leads), change: '+12%', up: true },
          { label: 'Won', value: formatNum(kpis.won_leads), change: '+8%', up: true },
          { label: 'Lost', value: formatNum(kpis.lost_leads), change: '-3%', up: false },
        ].map((kpi, i) => (
          <div key={i} className="oaa-card p-4" data-testid={`kpi-${kpi.label.toLowerCase()}`}>
            <p className="text-[11px] uppercase tracking-wider font-semibold text-neutral-400 mb-1">{kpi.label}</p>
            <div className="flex items-end gap-2">
              <p className="text-2xl font-bold text-[#0A0A0A] tabular-nums">{kpi.value}</p>
              {kpi.change && (
                <span className={`flex items-center gap-0.5 text-xs font-medium ${kpi.up ? 'text-emerald-500' : 'text-red-500'}`}>
                  {kpi.up ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                  {kpi.change}
                </span>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Revenue Bar */}
      <div className="oaa-card p-5" data-testid="revenue-bar">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-base font-semibold text-[#0A0A0A]">Revenue 2025</h3>
          <span className="text-sm text-neutral-400">${formatNum(totalRevenue)} / ${formatNum(targetRevenue)}</span>
        </div>
        <div className="h-3 bg-neutral-100 rounded-full overflow-hidden">
          <div className="h-full bg-gradient-to-r from-[#F66922] to-[#F59E0B] rounded-full transition-all" style={{ width: `${Math.min((totalRevenue / targetRevenue) * 100, 100)}%` }} />
        </div>
      </div>

      {/* Two Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Monthly Target vs Accepted */}
        <div className="oaa-card p-5" data-testid="monthly-chart">
          <h3 className="text-base font-semibold text-[#0A0A0A] mb-4">Month-Wise Target vs Accepted</h3>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={monthly} margin={{ bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
              <XAxis dataKey="month" tick={{ fontSize: 10, fill: '#94A3B8' }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 10, fill: '#94A3B8' }} axisLine={false} tickLine={false} />
              <Tooltip contentStyle={chartTooltipStyle} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Bar dataKey="target" name="Target" fill="#FDBA74" radius={[3, 3, 0, 0]} barSize={14} />
              <Bar dataKey="accepted" name="Accepted" fill="#F66922" radius={[3, 3, 0, 0]} barSize={14} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Geo Map */}
        <div className="oaa-card p-5" data-testid="campaign-geo-map">
          <h3 className="text-base font-semibold text-[#0A0A0A] mb-4">Targeted Geographies</h3>
          <div className="h-[260px] rounded-lg overflow-hidden">
            <MapContainer center={[20, 0]} zoom={2} style={{ height: '100%', width: '100%' }} scrollWheelZoom={false}>
              <TileLayer attribution="&copy; OpenStreetMap, &copy; CartoDB" url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png" />
              {geo.map((m, i) => (
                <CircleMarker key={i} center={[m.lat, m.lng]} radius={Math.max(6, Math.min(m.count / 100, 30))}
                  pathOptions={{ fillColor: '#F66922', fillOpacity: 0.6, color: '#F66922', weight: 1 }}>
                  <Popup><span className="text-xs font-medium">{m.country}: {formatNum(m.count)}</span></Popup>
                </CircleMarker>
              ))}
            </MapContainer>
          </div>
        </div>
      </div>

      {/* Daily chart + Sellers */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="oaa-card p-5" data-testid="daily-chart">
          <h3 className="text-base font-semibold text-[#0A0A0A] mb-4">Day-Wise Accepted vs Rejected</h3>
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={dailyData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
              <XAxis dataKey="day" tick={{ fontSize: 10, fill: '#94A3B8' }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 10, fill: '#94A3B8' }} axisLine={false} tickLine={false} />
              <Tooltip contentStyle={chartTooltipStyle} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Line type="monotone" dataKey="accepted" name="Accepted" stroke="#F66922" strokeWidth={2} dot={{ r: 2 }} />
              <Line type="monotone" dataKey="rejected" name="Rejected" stroke="#94A3B8" strokeWidth={2} dot={{ r: 2 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>

        <div className="oaa-card p-5" data-testid="seller-performance">
          <h3 className="text-base font-semibold text-[#0A0A0A] mb-4">Seller Performance</h3>
          <table className="data-table w-full">
            <thead>
              <tr>
                <th>Seller</th>
                <th className="text-right">Total</th>
                <th className="text-right">Accepted</th>
                <th className="text-right">Won</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {sellers.map((s, i) => (
                <tr key={i}>
                  <td className="font-medium">{s.seller}</td>
                  <td className="text-right tabular-nums">{formatNum(s.total_leads)}</td>
                  <td className="text-right tabular-nums">{formatNum(s.accepted)}</td>
                  <td className="text-right tabular-nums">{formatNum(s.won)}</td>
                  <td>
                    <div className="flex gap-1">
                      <button className="p-1 text-neutral-300 hover:text-neutral-500"><Download className="w-3.5 h-3.5" /></button>
                      <button className="p-1 text-neutral-300 hover:text-neutral-500"><Eye className="w-3.5 h-3.5" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Campaign Performance Table */}
      <div className="oaa-card p-5" data-testid="campaign-table">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <h3 className="text-base font-semibold text-[#0A0A0A]">Campaign Performance</h3>
            <div className="flex bg-neutral-100 rounded-lg overflow-hidden ml-3">
              {['Active', 'Inactive', 'All'].map(t => (
                <button key={t} onClick={() => setTab(t)}
                  className={`px-3 py-1 text-xs font-medium transition-all ${tab === t ? 'bg-[#F66922] text-white' : 'text-neutral-500 hover:bg-slate-200'}`}>
                  {t.toUpperCase()}
                </button>
              ))}
            </div>
          </div>
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-neutral-300" />
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search campaigns..."
              className="h-8 pl-8 pr-3 rounded-lg border border-neutral-200 text-xs focus:ring-2 focus:ring-[#F66922] outline-none w-48" />
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="data-table w-full">
            <thead>
              <tr>
                <th>#</th>
                <th>Campaign Name</th>
                <th className="text-right">Assigned</th>
                <th className="text-right">Accepted</th>
                <th className="text-right">Won</th>
                <th className="text-right">Meetings</th>
                <th className="text-right">Rev/Lead</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {campaigns.map((c, i) => (
                <tr key={i}>
                  <td className="text-neutral-300 tabular-nums">{(page - 1) * 25 + i + 1}</td>
                  <td className="font-medium text-neutral-800 max-w-[300px] truncate" title={c.name}>{c.name}</td>
                  <td className="text-right tabular-nums">{formatNum(c.leads_assigned)}</td>
                  <td className="text-right tabular-nums">{formatNum(c.accepted)}</td>
                  <td className="text-right tabular-nums">{formatNum(c.won)}</td>
                  <td className="text-right tabular-nums">{c.meetings_scheduled}</td>
                  <td className="text-right tabular-nums font-medium">${c.revenue_per_lead?.toFixed(0)}</td>
                  <td>
                    <span className={`status-badge ${c.status === 'Active' ? 'active' : 'lost'}`}>{c.status}</span>
                  </td>
                  <td>
                    <div className="flex gap-1">
                      <button className="p-1 text-neutral-300 hover:text-neutral-500"><Download className="w-3.5 h-3.5" /></button>
                      <button className="p-1 text-neutral-300 hover:text-neutral-500"><Eye className="w-3.5 h-3.5" /></button>
                      <button className="p-1 text-neutral-300 hover:text-neutral-500"><Settings className="w-3.5 h-3.5" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="flex items-center justify-between mt-3 pt-3 border-t border-neutral-100">
          <span className="text-xs text-neutral-400">{data?.total} campaigns total</span>
          <div className="flex items-center gap-2">
            <button disabled={page <= 1} onClick={() => setPage(page - 1)} className="p-1.5 rounded border border-neutral-200 hover:bg-neutral-50 disabled:opacity-40"><ChevronLeft className="w-4 h-4" /></button>
            <span className="text-xs text-neutral-500">Page {page} of {data?.pages}</span>
            <button disabled={page >= (data?.pages || 1)} onClick={() => setPage(page + 1)} className="p-1.5 rounded border border-neutral-200 hover:bg-neutral-50 disabled:opacity-40"><ChevronRight className="w-4 h-4" /></button>
          </div>
        </div>
      </div>
    </div>
  );
}
